import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UsersService } from '../users.service';
import { User } from '../users.type';
import { PermissionArea, PermissionLevel, Permissions } from '../../auth/auth.type';

type Level = PermissionLevel | 'none';

const AREAS: { area: PermissionArea; label: string; cards: string }[] = [
  { area: 'contracts', label: 'Contracts', cards: 'Orders, Buy Contracts, Stocks' },
  { area: 'suppliers_payments', label: 'Suppliers & payments', cards: 'Suppliers, Supplier Settings, Payments, Accounting' },
  { area: 'system', label: 'System', cards: 'Players, Proxies, Settings' },
  { area: 'accounts_reports', label: 'Accounts & reports', cards: 'Login Info, Reports' },
];

@Component({
  selector: 'app-manage-permissions-dialog',
  imports: [CommonModule, FormsModule, MatDialogModule, MatButtonModule, MatButtonToggleModule, MatProgressSpinnerModule],
  template: `
    <h2 mat-dialog-title>Permissions — {{ data.user.username }}</h2>

    <mat-dialog-content>
      @if (loading) {
        <div class="spinner-wrap"><mat-spinner diameter="32"></mat-spinner></div>
      } @else {
        <p class="hint">Read allows viewing only. Write also allows creating, editing and deleting.</p>
        @for (a of areas; track a.area) {
          <div class="area-row">
            <div class="area-text">
              <span class="area-label">{{ a.label }}</span>
              <span class="area-cards">{{ a.cards }}</span>
            </div>
            <mat-button-toggle-group [(ngModel)]="levels[a.area]" [attr.aria-label]="a.label" hideSingleSelectionIndicator>
              <mat-button-toggle value="none">None</mat-button-toggle>
              <mat-button-toggle value="read">Read</mat-button-toggle>
              <mat-button-toggle value="write">Write</mat-button-toggle>
            </mat-button-toggle-group>
          </div>
        }
      }
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button (click)="dialogRef.close(false)">Cancel</button>
      <button mat-flat-button color="primary" [disabled]="loading || saving" (click)="save()">
        {{ saving ? 'Saving…' : 'Save' }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .spinner-wrap { display: flex; justify-content: center; padding: 24px; }
    .hint { color: var(--mat-sys-on-surface-variant, #555); margin: 0 0 12px; }
    .area-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; padding: 10px 0; }
    .area-row + .area-row { border-top: 1px solid var(--mat-sys-outline-variant, #ddd); }
    .area-text { display: flex; flex-direction: column; min-width: 180px; flex: 1; }
    .area-label { font-weight: 600; }
    .area-cards { font-size: 12px; color: var(--mat-sys-on-surface-variant, #666); }
  `]
})
export class ManagePermissionsDialog implements OnInit {
  readonly dialogRef = inject(MatDialogRef<ManagePermissionsDialog>);
  readonly data = inject<{ user: User }>(MAT_DIALOG_DATA);
  private usersService = inject(UsersService);
  private snackBar = inject(MatSnackBar);

  readonly areas = AREAS;
  levels: Record<PermissionArea, Level> = { contracts: 'none', suppliers_payments: 'none', system: 'none', accounts_reports: 'none' };
  loading = true;
  saving = false;

  ngOnInit(): void {
    this.usersService.getUserPermissions(this.data.user.user_id).subscribe({
      next: res => {
        for (const a of AREAS) {
          this.levels[a.area] = res.result.permissions[a.area] ?? 'none';
        }
        this.loading = false;
      },
      error: () => {
        this.snackBar.open('Error loading permissions', 'Close', { duration: 4000 });
        this.loading = false;
      }
    });
  }

  save(): void {
    const permissions: Permissions = {};
    for (const a of AREAS) {
      const level = this.levels[a.area];
      if (level !== 'none') {
        permissions[a.area] = level;
      }
    }
    this.saving = true;
    this.usersService.setUserPermissions(this.data.user.user_id, permissions).subscribe({
      next: () => {
        this.snackBar.open('Permissions saved', 'Close', { duration: 3000 });
        this.dialogRef.close(true);
      },
      error: err => {
        this.snackBar.open(err.error?.error || 'Error saving permissions', 'Close', { duration: 5000 });
        this.saving = false;
      }
    });
  }
}
